import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { t } from "@/i18n";
import { toast } from "sonner";
import { Copy, Upload, X } from "lucide-react";

export default function Settings() {
  const [rest, setRest] = useState(null);
  const [form, setForm] = useState(null);
  const logoInputRef = useRef(null);

  const load = async () => {
    const { data } = await api.get("/restaurant");
    setRest(data);
    setForm({
      name: data.name,
      tagline: data.tagline || "",
      address: data.address || "",
      description: data.description || "",
      logo_url: data.logo_url || "",
      settings: data.settings || {},
    });
  };
  useEffect(() => { load(); }, []);

  if (!form) return <div className="p-10 text-sm text-[var(--text-muted)]">{t("common.loading")}</div>;

  const save = async () => {
    try {
      await api.patch("/restaurant", form);
      toast.success("Gespeichert");
      load();
    } catch {
      toast.error("Speichern fehlgeschlagen");
    }
  };

  const handleLogoFile = (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Bitte eine Bilddatei wählen.");
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Logo darf max. 2 MB gross sein.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, logo_url: reader.result }));
    reader.onerror = () => toast.error("Datei konnte nicht gelesen werden.");
    reader.readAsDataURL(file);
  };

  const bookingUrl = `${window.location.origin}/r/${rest.slug}`;
  const copyLink = () => {
    navigator.clipboard.writeText(bookingUrl);
    toast.success("Link kopiert");
  };

  return (
    <div className="px-6 lg:px-10 py-10 max-w-3xl mx-auto">
      <div className="mb-10">
        <h1 className="font-editorial italic text-5xl sm:text-6xl leading-[1.05] tracking-tight text-[var(--text-primary)]" data-testid="settings-title">{t("settings.title")}</h1>
      </div>

      <div className="space-y-6">
        <section className="surface-1 rounded-md p-6">
          <h2 className="font-heading text-lg font-normal mb-6">{t("settings.general")}</h2>
          <div className="space-y-4">
            <Field label={t("settings.restaurant_name")}>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="settings-name-input" className="input" />
            </Field>
            <Field label={t("settings.tagline")}>
              <input value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} data-testid="settings-tagline-input" className="input" />
            </Field>
            <Field label={t("settings.address")}>
              <input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="settings-address-input" className="input" />
            </Field>
            <Field label="Logo">
              <div className="flex items-center gap-4">
                <div className="w-24 h-24 rounded-md border border-white/10 bg-[#0F0D0A] flex items-center justify-center overflow-hidden flex-shrink-0">
                  {form.logo_url ? (
                    <img src={form.logo_url} alt="Logo" className="max-w-full max-h-full object-contain" data-testid="settings-logo-preview" />
                  ) : (
                    <span className="text-[10px] text-[var(--text-dim)] uppercase tracking-[0.18em]">Kein Logo</span>
                  )}
                </div>
                <div className="flex-1 flex flex-col gap-2">
                  <input
                    ref={logoInputRef}
                    type="file"
                    accept="image/*"
                    onChange={(e) => { handleLogoFile(e.target.files?.[0]); e.target.value = ""; }}
                    className="hidden"
                    data-testid="settings-logo-input"
                  />
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => logoInputRef.current?.click()}
                      data-testid="settings-logo-upload"
                      className="btn-ghost text-xs inline-flex items-center gap-2"
                    >
                      <Upload className="w-3.5 h-3.5" /> {form.logo_url ? "Ersetzen" : "Hochladen"}
                    </button>
                    {form.logo_url && (
                      <button
                        type="button"
                        onClick={() => setForm({ ...form, logo_url: "" })}
                        data-testid="settings-logo-remove"
                        className="btn-ghost text-xs inline-flex items-center gap-2 text-[#C98577]"
                      >
                        <X className="w-3.5 h-3.5" /> Entfernen
                      </button>
                    )}
                  </div>
                  <div className="text-[10px] text-[var(--text-dim)]">PNG, JPG oder SVG · max. 2 MB · wird auf der Buchungsmaske angezeigt.</div>
                </div>
              </div>
            </Field>
            <Field label={t("settings.description")}>
              <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="settings-description-input" rows={3} className="input !h-auto py-3" />
            </Field>
          </div>
        </section>

        <section className="surface-1 rounded-md p-6">
          <h2 className="font-heading text-lg font-normal mb-6">{t("settings.booking_settings")}</h2>
          <div className="space-y-4">
            <label className="flex items-center justify-between cursor-pointer">
              <span className="text-sm text-[var(--text-muted)]">{t("settings.guest_can_choose_table")}</span>
              <input type="checkbox" checked={form.settings.guest_can_choose_table || false} onChange={(e) => setForm({ ...form, settings: { ...form.settings, guest_can_choose_table: e.target.checked } })} data-testid="settings-guest-table-toggle" className="accent-gold w-5 h-5" />
            </label>
            <label className="flex items-center justify-between cursor-pointer">
              <span className="text-sm text-[var(--text-muted)]">{t("settings.lounge_enabled")}</span>
              <input type="checkbox" checked={form.settings.lounge_enabled !== false} onChange={(e) => setForm({ ...form, settings: { ...form.settings, lounge_enabled: e.target.checked } })} data-testid="settings-lounge-toggle" className="accent-gold w-5 h-5" />
            </label>
            <Field label={t("settings.confirmation_mode")}>
              <select value={form.settings.confirmation_mode || "auto"} onChange={(e) => setForm({ ...form, settings: { ...form.settings, confirmation_mode: e.target.value } })} data-testid="settings-confirmation-mode" className="input">
                <option value="auto">{t("settings.confirmation_modes.auto")}</option>
                <option value="manual">{t("settings.confirmation_modes.manual")}</option>
                <option value="smart">{t("settings.confirmation_modes.smart")}</option>
              </select>
            </Field>
          </div>
        </section>

        <section className="surface-1 rounded-md p-6">
          <h2 className="font-heading text-lg font-normal mb-4">{t("settings.booking_link")}</h2>
          <div className="flex items-center gap-2">
            <div className="flex-1 bg-[var(--bg-rooftop)] border border-white/10 rounded-md px-4 h-11 flex items-center text-sm text-gold font-mono" data-testid="booking-url">{bookingUrl}</div>
            <button onClick={copyLink} data-testid="copy-booking-url" className="btn-ghost text-xs inline-flex items-center gap-2"><Copy className="w-3.5 h-3.5" />Kopieren</button>
          </div>
        </section>

        <div className="flex justify-end">
          <button onClick={save} data-testid="settings-save-btn" className="btn-primary text-xs">{t("common.save")}</button>
        </div>
      </div>
      <style>{`.input{width:100%;background:#141414;border:1px solid rgba(255,255,255,0.1);border-radius:6px;padding:0 16px;height:44px;font-size:14px;color:#F5F5F0;outline:none;font-family:inherit;}.input:focus{border-color:#D4AF37;}`}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs text-[var(--text-muted)] mb-2 block uppercase tracking-wider">{label}</label>
      {children}
    </div>
  );
}
